/**
 * Per-task verification config shared by TaskForm, the manager settings table
 * and the mock task store. Mirrors the server's `verification` object and its
 * defaults when the field is absent, so every writer sends the same typed shape.
 */

export const VERIFICATION_METHODS = [
    { value: 'none', label: 'None' },
    { value: 'confirm', label: 'Confirm' },
    { value: 'photo', label: 'Photo' },
    { value: 'headsets_off', label: 'Headsets off' },
    { value: 'headsets_on', label: 'Headsets on' },
    { value: 'testimonial', label: 'Testimonials' },
];

/** What the server counts a testimonial task against when it sends no target. */
export const TESTIMONIAL_TARGET = 2;

export const isTestimonialTask = (task) => task?.verification?.method === 'testimonial';

/** `testimonialCount` is my own entries today; both ride on the task. */
export const testimonialProgress = (task) => ({
    count: Number(task?.testimonialCount) || 0,
    target: Number(task?.testimonialTarget) || TESTIMONIAL_TARGET,
});

/**
 * Header line for a `list_member_testimonials` read. It counts `mine` so it
 * reads the same as the task card; the gym total only rides along when someone
 * else recorded one, since the task is per trainer.
 */
export const testimonialDayLabel = (data) => {
    if (!data) return '';
    const mine = Number(data.mine) || 0;
    const team = Array.isArray(data.testimonials) ? data.testimonials.length : mine;
    const label = `${mine} ${mine === 1 ? 'testimonial' : 'testimonials'} today`;
    return team > mine ? `${label} (team ${team})` : label;
};

export const PHOTO_POLICIES = [
    { value: 'required', label: 'Required' },
    { value: 'optional', label: 'Optional' },
];

/** Which guided flow a task belongs to; empty means "follow the shift phase". */
export const FLOW_KINDS = [
    { value: '', label: 'Follow shift phase' },
    { value: 'open', label: 'Open' },
    { value: 'close', label: 'Close' },
    { value: 'cleaning', label: 'Cleaning' },
];

export const VERIFICATION_DEFAULTS = {
    method: 'none',
    photoPolicy: 'required',
    aiCheck: true,
    photoPrompt: '',
    checkPrompt: '',
    sampleRate: 1,
    // The SOP checklist is a guide: Done is one tap and unticked steps are a
    // soft audit finding. A manager opts one task back into the hard gate.
    requireSopSteps: false,
    // Lock-up work (arm the alarm, shut the booths down) that only counts once
    // the last member is out. Checking it off early asks the trainer whether
    // members have gone; the answer rides on the completion as
    // `meta.membersGone` and the nightly audit judges it.
    afterLastMember: false,
};

const METHOD_VALUES = VERIFICATION_METHODS.map((m) => m.value);
const FLOW_KIND_VALUES = FLOW_KINDS.map((k) => k.value);

export const withVerificationDefaults = (verification) => {
    const v = {
        ...VERIFICATION_DEFAULTS,
        ...(verification && typeof verification === 'object' ? verification : {}),
    };
    // With photos off the server serves a photo task as `confirm` for trainer
    // reads; an edit that round-trips that projection would overwrite the
    // stored method, so the real one is restored before any save.
    return v.photoDisabled ? { ...v, method: 'photo' } : v;
};

export const normalizeVerification = (input) => {
    const v = withVerificationDefaults(input);
    const rate = Number(v.sampleRate);
    return {
        method: METHOD_VALUES.includes(v.method) ? v.method : 'none',
        photoPolicy: v.photoPolicy === 'optional' ? 'optional' : 'required',
        aiCheck: Boolean(v.aiCheck),
        photoPrompt: String(v.photoPrompt || '').trim(),
        checkPrompt: String(v.checkPrompt || '').trim(),
        sampleRate: Number.isFinite(rate) ? Math.min(1, Math.max(0, rate)) : 1,
        requireSopSteps: Boolean(v.requireSopSteps),
        afterLastMember: Boolean(v.afterLastMember),
    };
};

/** Form/table inputs hand back any string; the server wants a known kind. */
export const parseFlowKind = (value) => {
    const kind = String(value ?? '').trim().toLowerCase();
    return FLOW_KIND_VALUES.includes(kind) ? kind : '';
};

/**
 * Walk order for anything carrying `flowOrder`: ascending, unordered tasks last,
 * ties broken by name so a reload never reshuffles. The one comparator, shared
 * by the wizard, the mock flow and the Review tab's settings table.
 */
export const compareFlowOrder = (a, b) => {
    const ao = a?.flowOrder == null ? Number.POSITIVE_INFINITY : Number(a.flowOrder);
    const bo = b?.flowOrder == null ? Number.POSITIVE_INFINITY : Number(b.flowOrder);
    if (ao !== bo) return ao - bo;
    return (a?.taskName || '').localeCompare(b?.taskName || '');
};

/** Form inputs hand back strings; the server wants an integer or null. */
export const parseFlowOrder = (value) => {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? Math.round(n) : null;
};
